import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../Layout';
import Plot from 'react-plotly.js';
import { listBaskets, getCompetitorData, parseCsvFile } from '../../services/trackingService';
import 'bootstrap/dist/css/bootstrap.min.css';

/* ──────────────────────────────────────────────── */
/* Rating Evolution — port of rating_evolution.py   */
/* ──────────────────────────────────────────────── */

function RatingEvolution() {
    const [baskets, setBaskets] = useState([]);
    const [selectedBasket, setSelectedBasket] = useState(null);
    const [data, setData] = useState([]);
    const [period, setPeriod] = useState('week');
    const [loading, setLoading] = useState(false);
    const [csvName, setCsvName] = useState(null);

    const accountId = localStorage.getItem('selectedAccountValue') || '';

    const loadBaskets = useCallback(async () => {
        if (!accountId) return;
        try { const res = await listBaskets(accountId); setBaskets(res.data || []); } catch { }
    }, [accountId]);

    useEffect(() => { loadBaskets(); }, [loadBaskets]);

    const loadData = useCallback(async () => {
        if (!selectedBasket || !accountId || selectedBasket.id === 'csv') return;
        setLoading(true);
        try { const res = await getCompetitorData(selectedBasket.id, accountId); setData(res.data || []); }
        catch { } finally { setLoading(false); }
    }, [selectedBasket, accountId]);

    useEffect(() => { loadData(); }, [loadData]);

    // CSV upload
    const handleCsvUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setLoading(true);
        try {
            const res = await parseCsvFile(file);
            setData(res.data || []);
            setCsvName(file.name);
            setSelectedBasket({ id: 'csv', name: file.name });
        } catch { }
        finally { setLoading(false); }
    };
    const handleClearCsv = () => { setCsvName(null); setSelectedBasket(null); setData([]); };

    const asins = [...new Set(data.map(r => r.asin).filter(Boolean))];
    const groupKey = period === 'day' ? 'date' : 'week';

    const buildTraces = () => {
        return asins.map(asin => {
            const rows = data.filter(r => r.asin === asin).sort((a, b) => new Date(a.date) - new Date(b.date));
            const brand = rows[0]?.brand || 'Unknown';
            return {
                x: rows.map(r => r[groupKey]),
                y: rows.map(r => r.product_star_rating != null ? Number(r.product_star_rating) : null),
                mode: 'lines+markers',
                name: `${brand} - ${asin}`,
                hovertemplate: `ASIN: ${asin}<br>Rating: %{y:.2f} ⭐<br>Reviews: %{customdata}<extra></extra>`,
                customdata: rows.map(r => r.product_num_ratings || 0),
            };
        });
    };

    return (
        <Layout>
            <div className="container-fluid py-4">
                <h2 className="fw-bold mb-4">Rating Evolution</h2>

                <div className="row mb-3">
                    <div className="col-md-4">
                        <select className="form-select" value={selectedBasket?.id || ''}
                            onChange={e => setSelectedBasket(baskets.find(b => b.id === parseInt(e.target.value)) || null)}>
                            <option value="">Select basket...</option>
                            {baskets.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                        </select>
                    </div>
                    <div className="col-md-3">
                        <div className="d-flex align-items-center gap-2">
                            <label className="btn btn-outline-info btn-sm mb-0" style={{ cursor: 'pointer' }}>
                                <i className="bi bi-filetype-csv me-1"></i>Upload CSV
                                <input type="file" accept=".csv" hidden onChange={handleCsvUpload} />
                            </label>
                            {csvName && (
                                <span className="badge bg-success d-flex align-items-center gap-1">
                                    {csvName}
                                    <i className="bi bi-x-lg" style={{ cursor: 'pointer' }} onClick={handleClearCsv}></i>
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="btn-group btn-group-sm w-100">
                            <button className={`btn ${period === 'week' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setPeriod('week')}>Weekly</button>
                            <button className={`btn ${period === 'day' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setPeriod('day')}>Daily</button>
                        </div>
                    </div>
                </div>

                {loading && <div className="text-center"><div className="spinner-border" /></div>}

                {data.length > 0 && (
                    <div className="card mb-4">
                        <div className="card-body">
                            <Plot
                                data={buildTraces()}
                                layout={{
                                    title: 'Star Rating Evolution',
                                    height: Math.max(400, 100 * Math.ceil(asins.length / 3)),
                                    xaxis: { title: { text: period === 'day' ? 'Date' : 'Week' }, ...(period === 'day' ? { type: 'date', tickformat: '%b %d' } : { type: 'category', tickprefix: 'Wk ' }) },
                                    yaxis: { title: { text: 'Star Rating' }, range: [0, 5.5] },
                                    margin: { l: 60, r: 20, t: 50, b: 55 },
                                }}
                                config={{ responsive: true }}
                                style={{ width: '100%' }}
                            />
                        </div>
                    </div>
                )}

                {/* ═══ Breakdown by ASIN — individual subplots grid ═══ */}
                {data.length > 0 && asins.length > 0 && (
                    <div className="card mb-4">
                        <div className="card-body">
                            <h5 className="mb-3">Rating Evolution (by ASIN)</h5>
                            <div className="row">
                                {asins.map(asin => {
                                    const asinData = data.filter(r => r.asin === asin).sort((a, b) => new Date(a.date) - new Date(b.date));
                                    const brand = asinData[0]?.brand || 'Unknown';
                                    return (
                                        <div className="col-md-4 mb-3" key={asin}>
                                            <Plot
                                                data={[{
                                                    x: asinData.map(r => r[groupKey]),
                                                    y: asinData.map(r => r.product_star_rating != null ? Number(r.product_star_rating) : null),
                                                    mode: 'lines+markers',
                                                    line: { width: 2 },
                                                    marker: { size: 6 },
                                                    hovertemplate: `Rating: %{y:.1f} ⭐<extra></extra>`,
                                                }]}
                                                layout={{
                                                    title: { text: `${brand} - ${asin}`, font: { size: 13, color: '#000' } },
                                                    height: 250,
                                                    xaxis: { title: { text: period === 'day' ? 'Date' : 'Week' }, showgrid: false, ...(period === 'day' ? { type: 'date', tickformat: '%b %d' } : { type: 'category', tickprefix: 'Wk ' }) },
                                                    yaxis: { title: { text: 'Rating' }, range: [0, 5.5], showgrid: true },
                                                    margin: { l: 60, r: 15, t: 35, b: 50 },
                                                }}
                                                config={{ responsive: true, displayModeBar: false }}
                                                style={{ width: '100%' }}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}

                {/* Pivot table (Rating History) */}
                {(() => {
                    const buildPivotTable = () => {
                        const keys = [...new Set(data.map(r => r[groupKey]))].sort((a, b) => period === 'week' ? Number(a) - Number(b) : a.localeCompare(b));
                        const rows = keys.map(key => {
                            const row = { [groupKey]: key };
                            asins.forEach(asin => {
                                const match = data.find(r => r.asin === asin && r[groupKey] === key);
                                row[`${asin}`] = match?.product_star_rating != null ? Number(match.product_star_rating).toFixed(1) : '—';
                            });
                            return row;
                        });
                        return rows;
                    };
                    const pivotData = data.length > 0 ? buildPivotTable() : [];
                    const pivotColumns = pivotData.length > 0 ? Object.keys(pivotData[0]) : [];

                    return pivotData.length > 0 && (
                        <div className="card mb-4">
                            <div className="card-header" id="headingHistory">
                                <h5 className="mb-0">
                                    <button className="btn btn-link text-decoration-none" data-bs-toggle="collapse" data-bs-target="#collapseHistory">
                                        Show rating table
                                    </button>
                                </h5>
                            </div>
                            <div id="collapseHistory" className="collapse">
                                <div className="card-body">
                                    <div className="table-responsive">
                                        <table className="table table-sm table-striped table-hover text-center">
                                            <thead className="table-dark">
                                                <tr>{pivotColumns.map(c => <th key={c}>{c}</th>)}</tr>
                                            </thead>
                                            <tbody>
                                                {pivotData.map((row, i) => (
                                                    <tr key={i}>{pivotColumns.map(c => <td key={c}>{row[c]}</td>)}</tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })()}

                {/* Rating summary table */}
                {data.length > 0 && (
                    <div className="card mt-4">
                        <div className="card-body">
                            <h5>Current Ratings</h5>
                            <div className="table-responsive">
                                <table className="table table-sm table-striped">
                                    <thead>
                                        <tr>
                                            <th>ASIN</th><th>Brand</th><th>Rating</th><th>Reviews</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {asins.map(asin => {
                                            const latest = data.filter(r => r.asin === asin).sort((a, b) => new Date(b.date) - new Date(a.date))[0];
                                            const star = latest?.product_star_rating != null ? Number(latest.product_star_rating) : null;
                                            const ratings = latest?.product_num_ratings != null ? Number(latest.product_num_ratings) : null;
                                            return (
                                                <tr key={asin}>
                                                    <td>{asin}</td>
                                                    <td>{latest?.brand || '—'}</td>
                                                    <td>⭐ {star != null ? star.toFixed(2) : '—'}</td>
                                                    <td>{ratings != null ? ratings.toLocaleString() : '—'}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
}

export default RatingEvolution;
